// src/pages/ProfessionalSellerOnboarding.tsx
import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next'; 
import { Seo } from '../../components/Seo';
import { Building2, Shield, User, MapPin, Tag, CreditCard, CheckCircle, Clock, ChevronRight, Upload, AlertCircle } from 'lucide-react';

const STEPS = [
  { label: 'Company', icon: Building2 },
  { label: 'Business Docs', icon: Shield },
  { label: 'Owner Info', icon: User },
  { label: 'Address', icon: MapPin },
  { label: 'Subscription', icon: Tag },
  { label: 'Payment', icon: CreditCard },
  { label: 'Pending', icon: Clock },
  { label: 'Approved', icon: CheckCircle },
];

const PLANS = [
  { id: 'starter', name: 'Starter', price: 499, listings: 20, features: ['20 listings', 'Basic CRM', 'Standard support'] },
  { id: 'business', name: 'Business', price: 999, listings: 52, features: ['52 listings', 'Full CRM', 'Priority support', 'Analytics'], popular: true },
  { id: 'enterprise', name: 'Enterprise', price: 1999, listings: -1, features: ['Unlimited listings', 'Full CRM', 'Dedicated support', 'Multi-branch', 'API access'] },
];

export function ProfessionalSellerOnboarding() {
  const { t } = useTranslation('auth'); 
  const [step, setStep] = useState(0);
  const [plan, setPlan] = useState('business');

  const next = () => setStep(s => Math.min(s + 1, 7));
  const prev = () => setStep(s => Math.max(s - 1, 0));

  // Helper function to get plan features array from translation
  const getPlanFeatures = (planId: string) => {
    return t(`professionalSellerOnboarding.step4.plans.${planId}.features`, { returnObjects: true }) as string[];
  };

  // Translate step labels for progress
  const translatedSteps = STEPS.map((s, index) => {
    const key = ['company', 'businessDocs', 'ownerInfo', 'address', 'subscription', 'payment', 'pending', 'approved'][index];
    return { ...s, label: t(`professionalSellerOnboarding.steps.${key}`) };
  });

  const UploadBox = ({ label }: { label: string }) => (
    <label className="block cursor-pointer">
      <p className="text-sm text-[#2D323A] mb-1.5">{label}</p>
      <div className="border-2 border-dashed border-[#E5E7EB] rounded-xl p-5 text-center hover:border-[#1E6DFF] transition-colors">
        <Upload className="w-7 h-7 text-[#BFC2C7] mx-auto mb-1" />
        <p className="text-sm text-[#717182]">{t('professionalSellerOnboarding.buttons.uploadClick')}</p>
        <p className="text-xs text-[#BFC2C7]">{t('professionalSellerOnboarding.buttons.uploadHint')}</p>
      </div>
      <input type="file" className="hidden" />
    </label>
  );

  const Input = ({ label, placeholder, type = 'text' }: { label: string; placeholder: string; type?: string }) => (
    <div>
      <label className="block text-sm text-[#2D323A] mb-1.5">{label}</label>
      <input type={type} placeholder={placeholder}
        className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] transition-colors" />
    </div>
  );

  return (
    <>
    <Seo
      title="Professional Seller Registration | Morrocars"
      description="Register your dealership on Morrocars, submit business and owner documents, choose a subscription plan, and start managing your vehicle inventory."
    />
    <div className="min-h-screen bg-[#F5F7FA] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl text-[#2D323A] mb-1">{t('professionalSellerOnboarding.title')}</h1>
          <p className="text-[#717182]">{t('professionalSellerOnboarding.step', { current: step + 1, total: STEPS.length })}</p>
        </div>

        {/* Progress */}
        <div className="flex items-center mb-8 overflow-x-auto pb-2">
          {translatedSteps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="flex items-center flex-1 last:flex-none flex-shrink-0">
                <div className="flex flex-col items-center">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${
                    i < step ? 'bg-[#22C55E] text-white' :
                    i === step ? 'bg-[#1E6DFF] text-white shadow-md' :
                    'bg-white border-2 border-[#E5E7EB] text-[#BFC2C7]'
                  }`}>
                    {i < step ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <span className={`text-xs mt-1 hidden md:block whitespace-nowrap ${i === step ? 'text-[#1E6DFF]' : 'text-[#717182]'}`}>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 transition-colors min-w-[20px] ${i < step ? 'bg-[#22C55E]' : 'bg-[#E5E7EB]'}`} />
                )}
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-[#E5E7EB]">
          {step === 0 && (
            <div>
              <h2 className="text-xl text-[#2D323A] mb-6">{t('professionalSellerOnboarding.step0.title')}</h2>
              <div className="space-y-4">
                <Input label={t('professionalSellerOnboarding.step0.companyName')} placeholder={t('professionalSellerOnboarding.step0.companyNamePlaceholder')} />
                <Input label={t('professionalSellerOnboarding.step0.tradeName')} placeholder={t('professionalSellerOnboarding.step0.tradeNamePlaceholder')} />
                <div className="grid grid-cols-2 gap-4">
                  <Input label={t('professionalSellerOnboarding.step0.registrationNumber')} placeholder={t('professionalSellerOnboarding.step0.registrationPlaceholder')} />
                  <Input label={t('professionalSellerOnboarding.step0.iceNumber')} placeholder={t('professionalSellerOnboarding.step0.icePlaceholder')} />
                </div>
                <Input label={t('professionalSellerOnboarding.step0.taxId')} placeholder={t('professionalSellerOnboarding.step0.taxPlaceholder')} />
                <div>
                  <label className="block text-sm text-[#2D323A] mb-1.5">{t('professionalSellerOnboarding.step0.businessType')}</label>
                  <select className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF]">
                    <option value="sarl">{t('professionalSellerOnboarding.step0.businessTypeOptions.sarl')}</option>
                    <option value="sa">{t('professionalSellerOnboarding.step0.businessTypeOptions.sa')}</option>
                    <option value="autoEntrepreneur">{t('professionalSellerOnboarding.step0.businessTypeOptions.autoEntrepreneur')}</option>
                    <option value="snc">{t('professionalSellerOnboarding.step0.businessTypeOptions.snc')}</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <h2 className="text-xl text-[#2D323A] mb-2">{t('professionalSellerOnboarding.step1.title')}</h2>
              <p className="text-[#717182] text-sm mb-6">{t('professionalSellerOnboarding.step1.subtitle')}</p>
              <div className="grid grid-cols-2 gap-4">
                <UploadBox label={t('professionalSellerOnboarding.step1.registrationCertificate')} />
                <UploadBox label={t('professionalSellerOnboarding.step1.businessLicense')} />
                <UploadBox label={t('professionalSellerOnboarding.step1.taxRegistration')} />
                <UploadBox label={t('professionalSellerOnboarding.step1.companyStatute')} />
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="text-xl text-[#2D323A] mb-6">{t('professionalSellerOnboarding.step2.title')}</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input label={t('professionalSellerOnboarding.step2.firstName')} placeholder={t('professionalSellerOnboarding.step2.firstNamePlaceholder')} />
                  <Input label={t('professionalSellerOnboarding.step2.lastName')} placeholder={t('professionalSellerOnboarding.step2.lastNamePlaceholder')} />
                </div>
                <Input label={t('professionalSellerOnboarding.step2.cinNumber')} placeholder={t('professionalSellerOnboarding.step2.cinPlaceholder')} />
                <Input label={t('professionalSellerOnboarding.step2.personalPhone')} placeholder={t('professionalSellerOnboarding.step2.phonePlaceholder')} />
                <Input label={t('professionalSellerOnboarding.step2.personalEmail')} placeholder={t('professionalSellerOnboarding.step2.emailPlaceholder')} type="email" />
                <div className="grid grid-cols-2 gap-4">
                  <UploadBox label={t('professionalSellerOnboarding.step2.cinFront')} />
                  <UploadBox label={t('professionalSellerOnboarding.step2.cinBack')} />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="text-xl text-[#2D323A] mb-6">{t('professionalSellerOnboarding.step3.title')}</h2>
              <div className="space-y-4">
                <Input label={t('professionalSellerOnboarding.step3.showroomAddress')} placeholder={t('professionalSellerOnboarding.step3.addressPlaceholder')} />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#2D323A] mb-1.5">{t('professionalSellerOnboarding.step3.city')}</label>
                    <select className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF]">
                      <option value="">{t('professionalSellerOnboarding.step3.cityPlaceholder')}</option>
                      {['Casablanca', 'Rabat', 'Marrakech', 'Fès', 'Tanger', 'Agadir'].map(c => {
                        const key = c === 'Fès' ? 'fes' : c.toLowerCase();
                        return <option key={c} value={c}>{t(`professionalSellerOnboarding.step3.cities.${key}`)}</option>;
                      })}
                    </select>
                  </div>
                  <Input label={t('professionalSellerOnboarding.step3.postalCode')} placeholder={t('professionalSellerOnboarding.step3.postalPlaceholder')} />
                </div>
                <Input label={t('professionalSellerOnboarding.step3.publicPhone')} placeholder={t('professionalSellerOnboarding.step3.publicPhonePlaceholder')} />
                <Input label={t('professionalSellerOnboarding.step3.website')} placeholder={t('professionalSellerOnboarding.step3.websitePlaceholder')} />
                <Input label={t('professionalSellerOnboarding.step3.whatsapp')} placeholder={t('professionalSellerOnboarding.step3.whatsappPlaceholder')} />
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 className="text-xl text-[#2D323A] mb-6">{t('professionalSellerOnboarding.step4.title')}</h2>
              <div className="space-y-4">
                {PLANS.map(p => {
                  const planKey = p.id;
                  const planName = t(`professionalSellerOnboarding.step4.plans.${planKey}.name`);
                  const price = t(`professionalSellerOnboarding.step4.plans.${planKey}.price`, { price: p.price });
                  const features = t(`professionalSellerOnboarding.step4.plans.${planKey}.features`, { returnObjects: true }) as string[];
                  return (
                    <label key={p.id} className={`flex gap-4 p-4 border-2 rounded-2xl cursor-pointer transition-all ${plan === p.id ? 'border-[#1E6DFF] bg-[#1E6DFF]/5' : 'border-[#E5E7EB] hover:border-[#BFC2C7]'}`}>
                      <input type="radio" name="plan" value={p.id} checked={plan === p.id} onChange={() => setPlan(p.id)} className="accent-[#1E6DFF] mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[#2D323A]">{planName}</span>
                          {p.popular && <span className="text-xs px-2 py-0.5 bg-[#1E6DFF] text-white rounded-lg">{t('professionalSellerOnboarding.step4.mostPopular')}</span>}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {features.map((f: string) => <span key={f} className="text-xs text-[#717182]">• {f}</span>)}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[#2D323A]">{price}</p>
                        <p className="text-xs text-[#717182]">{t('professionalSellerOnboarding.step4.perMonth')}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <h2 className="text-xl text-[#2D323A] mb-4">{t('professionalSellerOnboarding.step5.title')}</h2>
              <div className="bg-[#F5F7FA] rounded-xl p-4 flex items-center justify-between mb-5">
                <span className="text-[#717182]">{t('professionalSellerOnboarding.step5.selectedPlan', { plan: t(`professionalSellerOnboarding.step4.plans.${plan}.name`) })}</span>
                <span className="text-[#1E6DFF]">{t(`professionalSellerOnboarding.step4.plans.${plan}.price`, { price: PLANS.find(p => p.id === plan)?.price })} {t('professionalSellerOnboarding.step4.perMonth')}</span>
              </div>
              <div className="space-y-4">
                <Input label={t('professionalSellerOnboarding.step5.cardNumber')} placeholder={t('professionalSellerOnboarding.step5.cardNumberPlaceholder')} />
                <Input label={t('professionalSellerOnboarding.step5.cardholderName')} placeholder={t('professionalSellerOnboarding.step5.cardholderPlaceholder')} />
                <div className="grid grid-cols-2 gap-4">
                  <Input label={t('professionalSellerOnboarding.step5.expiry')} placeholder={t('professionalSellerOnboarding.step5.expiryPlaceholder')} />
                  <Input label={t('professionalSellerOnboarding.step5.cvv')} placeholder={t('professionalSellerOnboarding.step5.cvvPlaceholder')} />
                </div>
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="text-center py-6">
              <div className="w-20 h-20 bg-[#F59E0B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-10 h-10 text-[#F59E0B]" />
              </div>
              <h2 className="text-2xl text-[#2D323A] mb-2">{t('professionalSellerOnboarding.step6.title')}</h2>
              <p className="text-[#717182] mb-6">{t('professionalSellerOnboarding.step6.subtitle')}</p>
              <div className="bg-[#F5F7FA] rounded-xl p-4 text-left mb-6">
                <p className="text-sm text-[#2D323A] mb-3">{t('professionalSellerOnboarding.step6.whatHappensNext')}</p>
                {(t('professionalSellerOnboarding.step6.nextSteps', { returnObjects: true }) as string[]).map((item, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-[#E5E7EB] last:border-0">
                    <div className="w-6 h-6 rounded-full bg-[#1E6DFF]/10 flex items-center justify-center text-xs text-[#1E6DFF]">{i + 1}</div>
                    <span className="text-sm text-[#717182]">{item}</span>
                  </div>
                ))}
              </div>
              <button onClick={next} className="w-full py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md">
                {t('professionalSellerOnboarding.step6.viewStatus')}
              </button>
            </div>
          )}

          {step === 7 && (
            <div className="text-center py-6">
              <div className="w-20 h-20 bg-[#22C55E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-[#22C55E]" />
              </div>
              <h2 className="text-2xl text-[#2D323A] mb-2">{t('step7.title')}</h2>
              <p className="text-[#717182] mb-6">{t('step7.subtitle')}</p>
              <div className="grid grid-cols-2 gap-3 mb-6 text-left">
                {[
                  { label: t('professionalSellerOnboarding.step7.summary.accountType'), value: t('professionalSellerOnboarding.step7.summary.verifiedDealer'), color: 'text-[#1E6DFF]' },
                  { label: t('professionalSellerOnboarding.step7.summary.subscription'), value: t(`professionalSellerOnboarding.step4.plans.${plan}.name`), color: 'text-[#22C55E]' },
                  { label: t('professionalSellerOnboarding.step7.summary.listings'), value: PLANS.find(p => p.id === plan)?.listings === -1 ? t('professionalSellerOnboarding.step7.summary.unlimited') : `${PLANS.find(p => p.id === plan)?.listings}`, color: 'text-[#2D323A]' },
                  { label: t('professionalSellerOnboarding.step7.summary.status'), value: t('professionalSellerOnboarding.step7.summary.active'), color: 'text-[#22C55E]' },
                ].map(item => (
                  <div key={item.label} className="bg-[#F5F7FA] rounded-xl p-3">
                    <p className="text-xs text-[#717182] mb-1">{item.label}</p>
                    <p className={`text-sm ${item.color}`}>{item.value}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-3">
                <Link to="/dealer" className="block w-full py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md">
                  {t('professionalSellerOnboarding.step7.buttons.goToDashboard')}
                </Link>
                <Link to="/dealer/add-vehicle" className="block w-full py-3 border border-[#E5E7EB] text-[#2D323A] rounded-xl hover:bg-[#F5F7FA] transition-colors">
                  {t('professionalSellerOnboarding.step7.buttons.addFirstVehicle')}
                </Link>
              </div>
            </div>
          )}

          {step < 6 && (
            <div className="flex gap-3 mt-8">
              {step > 0 && (
                <button onClick={prev} className="px-6 py-3 border border-[#E5E7EB] text-[#2D323A] rounded-xl hover:bg-[#F5F7FA] transition-colors">
                  {t('professionalSellerOnboarding.buttons.back')}
                </button>
              )}
              <button onClick={next} className="flex-1 py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md flex items-center justify-center gap-2">
                {step === 5 ? t('professionalSellerOnboarding.buttons.submitAndPay') : t('professionalSellerOnboarding.buttons.continue')}
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}